import React from "react";

import { Loader2 } from "lucide-react";

import { ACCENT, Modal } from "./Chrome";
import KpiCategoryPaneBody from "./KpiCategoryPaneBody";
import {
  buildMetricLookup,
  formatDMY,
  HeroScoreBubble,
} from "./KpiDetailBlocks";
import type {
  XFactorKpiCategoryDetailModel,
  XFactorKpiFullBriefing,
} from "../lib/deriveKpi";

const tabLabel = (categoryId: string, fallback: string) => {
  if (!categoryId) return fallback;
  return categoryId
    .split(/[_-]+/)
    .filter(Boolean)
    .map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(" ");
};

type Props = {
  open: boolean;
  onClose: () => void;
  accountName: string;
  accountId?: string | null;
  briefing: XFactorKpiFullBriefing | null;
  loading: boolean;
  error?: string | null;
};

/**
 * KPI Metrics Full Briefing — Overall + every category in one continuous
 * feed. Scroll / sticky tabs / scroll-spy match Business Health
 * FullBriefingModal (modal body scrolls; tabs are anchors, not panes).
 */
const KpiFullBriefingModal: React.FC<Props> = ({
  open,
  onClose,
  accountName,
  accountId = null,
  briefing,
  loading,
  error,
}) => {
  const panes = React.useMemo(() => {
    if (!briefing) return [] as XFactorKpiCategoryDetailModel[];
    return [
      ...(briefing.overall ? [briefing.overall] : []),
      ...briefing.categories,
    ];
  }, [briefing]);

  const tabs = React.useMemo(
    () =>
      panes.map(p => ({
        id: p.categoryId,
        label: tabLabel(p.categoryId, p.categoryName),
        longLabel:
          p.categoryId === "overall"
            ? "Overall"
            : p.categoryName || tabLabel(p.categoryId, p.categoryId),
        missing: !p.hasData,
      })),
    [panes]
  );

  const [active, setActive] = React.useState("overall");
  const paneRefs = React.useRef<Record<string, HTMLElement | null>>({});

  const briefingMatchesAccount =
    !accountId ||
    !briefing?.accountId ||
    String(briefing.accountId) === String(accountId);
  const showLoading =
    loading || (Boolean(accountId) && !briefingMatchesAccount);
  const lookup = React.useMemo(
    () => buildMetricLookup(briefingMatchesAccount ? briefing : null),
    [briefing, briefingMatchesAccount]
  );
  const measurementDate = briefingMatchesAccount
    ? (briefing?.date ?? null)
    : null;

  React.useEffect(() => {
    if (open) setActive("overall");
  }, [open, accountName, accountId]);

  /**
   * Same scroll-spy as Business Health FullBriefingModal: viewport root,
   * margins pick the section crossing the upper fifth of the screen.
   */
  React.useEffect(() => {
    if (!open || showLoading || !panes.length) return undefined;
    const observer = new IntersectionObserver(
      entries => {
        const hit = entries
          .filter(e => e.isIntersecting)
          .sort(
            (a, b) => a.boundingClientRect.top - b.boundingClientRect.top
          )[0];
        const id = hit?.target.getAttribute("data-section");
        if (id) setActive(id);
      },
      { rootMargin: "-20% 0px -70% 0px", threshold: 0 }
    );
    Object.values(paneRefs.current).forEach(el => el && observer.observe(el));
    return () => observer.disconnect();
  }, [open, panes, showLoading]);

  const jump = (sectionId: string) => {
    setActive(sectionId);
    paneRefs.current[sectionId]?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  };

  const heroScore = briefingMatchesAccount
    ? (briefing?.overall?.score ?? null)
    : null;

  return (
    <Modal
      open={open}
      onClose={onClose}
      width="max-w-5xl"
      title={
        <span className="flex items-center gap-4">
          <HeroScoreBubble score={heroScore} />
          <span className="min-w-0 truncate text-xl font-bold leading-tight">
            KPI Metrics Full Briefing:{" "}
            <span style={{ color: ACCENT }}>{accountName}</span>
          </span>
        </span>
      }
      subtitle={
        briefing?.generatedAt
          ? `Briefing date: ${formatDMY(briefing.generatedAt)}`
          : measurementDate
            ? `Measurement date: ${formatDMY(measurementDate)}`
            : undefined
      }
    >
      {showLoading ? (
        <div className="flex items-center gap-2 py-8 text-sm text-gray-500">
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
          Loading full briefing…
        </div>
      ) : error ? (
        <p className="m-0 text-sm text-amber-700">{error}</p>
      ) : !briefingMatchesAccount || !briefing || !panes.length ? (
        <div className="rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          This account has no KPI briefing yet for this measurement date.
        </div>
      ) : (
        <>
          {/* Same sticky tab strip as Business Health FullBriefingModal. */}
          <div className="sticky -top-5 z-20 -mx-6 mb-4 border-b border-gray-200 bg-white px-6 pt-5">
            <div className="flex flex-wrap gap-x-4">
              {tabs.map(t => {
                const isActive = t.id === active;
                return (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() => jump(t.id)}
                    className="-mb-px border-b-2 px-0.5 pb-2 text-sm transition-colors"
                    style={{
                      borderColor: isActive ? ACCENT : "transparent",
                      color: isActive
                        ? ACCENT
                        : t.missing
                          ? "#cbd5e1"
                          : "#475569",
                      fontWeight: isActive ? 600 : 400,
                    }}
                  >
                    {t.label}
                  </button>
                );
              })}
            </div>
          </div>

          {panes.map((pane, index) => {
            const meta = tabs[index];
            return (
              <section
                key={pane.categoryId}
                data-section={pane.categoryId}
                ref={el => {
                  paneRefs.current[pane.categoryId] = el;
                }}
                className={`scroll-mt-16 ${
                  index ? "mt-8 border-t border-gray-100 pt-6" : ""
                }`}
              >
                <div className="mb-3 flex items-center gap-3">
                  {pane.categoryId !== "overall" ? (
                    <HeroScoreBubble score={pane.score} />
                  ) : null}
                  <h3
                    className="mb-0 min-w-0 flex-1 truncate text-sm font-semibold"
                    style={{ color: ACCENT }}
                  >
                    {meta?.longLabel ?? pane.categoryName}
                  </h3>
                </div>
                {!pane.hasData ? (
                  <div className="rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
                    <span className="font-medium">
                      No {pane.categoryName} data for this measurement date.
                    </span>{" "}
                    {pane.emptyReason === "missing_inputs"
                      ? "Required metrics haven't been scored yet."
                      : pane.emptyReason || ""}
                  </div>
                ) : (
                  <KpiCategoryPaneBody
                    category={pane}
                    lookup={lookup}
                    measurementDate={measurementDate}
                    scope={`kpi-fb-${pane.categoryId}`}
                  />
                )}
              </section>
            );
          })}
        </>
      )}
    </Modal>
  );
};

export default KpiFullBriefingModal;
